import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  switchMap,
  tap,
} from 'rxjs/operators';

import { EntityService } from '../../../services/entity.service';
import { ProductService } from '../../../services/product.service';
import { CostCenter } from '../../../models/cost-center.model';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { OrderService } from '../../../services/order.service';
import { Entity } from '../../../models/entity.model';
import { Order } from '../../../models/order.model';
import { CreditCard } from '../../../models/credit-card.model';
import { PaymentMethod } from '../../../models/payment-method.model';
import { PaymentService } from '../../../services/payments.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../../../services/auth.service';
import { OrderTypes } from '../../../enums/order-types';
import { DecisionPointData } from '../../../interfaces/decision-point.data';

enum SAP_TYPE {
  STANDARD,
  DECISION_POINT,
  WHOLESALER,
}

// submit sap number event
export interface SapCostDataEvent {
  costCenter: CostCenter;
  decisionPointId?: number;
}

@UntilDestroy()
@Component({
  selector: 'app-sap-modal',
  templateUrl: './sap-modal.component.html',
  styleUrls: ['./sap-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SapModalComponent implements OnInit {
  @Input() orderType: string;

  SapType = SAP_TYPE;
  isSapType: SAP_TYPE = SAP_TYPE.STANDARD;

  costCenter: CostCenter;
  creditCard: CreditCard;

  // decision point search
  dpSearchResults: DecisionPointData[] = [];
  dpSearchLoading = false;
  dpSearchSubject: Subject<string> = new Subject<string>();
  dpSelection: DecisionPointData = { id: '0', wslr_count: 0, store_count: 0 };
  dpSelectionId: string;

  // wholesaler search
  wholesalerSearchResults: Entity[] = [];
  wholesalerSearchLoading = false;
  wholesalerSearchSubject: Subject<string> = new Subject<string>();
  wholesalerSelection: any = { id: 0 };
  wholesalerSelectionId: number;

  closeWithWarning = false;
  canChangeOrderType = true;

  originalOrderName = '';
  orderName = '';
  dpOrderName = '';
  wslrOrderName = '';
  wslrTaxExempt = false; // tax exempt for WSLR orders is true by default
  isWslrTaxExemptAllowed = false;

  poNumber: string;
  attention: string;
  originalAttention: string;
  taxExempt = false;

  order: Order;

  inProgress = false;
  saveCard = false;

  headerLabel = '';

  private attentionFieldClicked = false;

  constructor(
    protected toastr: ToastrService,
    private entityService: EntityService,
    private productService: ProductService,
    private paymentService: PaymentService,
    private orderService: OrderService,
    private activeModal: NgbActiveModal,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initDPSearch();
    this.initWholesalerSearch();
    this.showSapModal(this.order);
  }

  // public methods

  // show modal dialog
  public showSapModal(order: Order): void {
    if (this.order) {
      this.orderName = this.order.attr_cart_name;
      this.originalOrderName = this.orderName;
      this.poNumber = this.order.attr_po_num || '';
      this.costCenter = this.order.costCenter;
      this.creditCard = this.order.creditCard;
      this.attention = this.order.isAttentionOverridden
        ? ''
        : this.order.attr_attention || '';
      this.originalAttention = this.attention;

      this.taxExempt = this.order.taxExempt;

      this.wslrOrderName = this.order.cartName;
      this.dpOrderName = this.orderName;

      // check if order is Decision Point
      if (this.order.isDecisionPoint) {
        this.isSapType = this.SapType.DECISION_POINT;

        if (this.order.decisionPointData) {
          this.dpSelection = this.order.decisionPointData;
        }
      } else if (this.order.isWSLR) {
        this.isSapType = this.SapType.WHOLESALER;

        // check if tax exempt is allowed
        this.wslrTaxExempt = this.order.taxExempt;
        if (this.order.entity) {
          this.isWslrTaxExemptAllowed = this.order.entity.isTaxExemptAllowed;
        }
      }
    }
  }

  get canSwitchOrderType(): boolean {
    if (!this.canChangeOrderType) {
      return false;
    }

    // if (this.order.type === CUSTOM_TYPE) {
    //     return false; // autoship order type cannot be changed
    // }

    if (
      this.order.item_count > 0 ||
      this.order.isWSLR ||
      this.order.isDecisionPoint
    ) {
      return false;
    }

    if (!this.authService.isEmployee) {
      return false;
    }

    return true;
  }

  // hide and reset modal dialog
  hideSapModal(order: Order): void {
    this.orderName = '';
    this.wholesalerSelection = { id: 0 };
    this.wholesalerSelectionId = this.wholesalerSelection.id;
    this.dpSelection = { id: '0', store_count: 0, wslr_count: 0 };
    this.dpSelectionId = this.dpSelection.id;
    this.isSapType = SAP_TYPE.STANDARD;

    if (this.closeWithWarning) {
      this.toastr.error(
        'Your item was not saved. You must complete the form before we can add items to this order.'
      );
      this.closeWithWarning = false;
    }

    this.activeModal.close(order);
  }

  get canApply(): boolean {
    if (this.verifyingSAP || this.updatingPaymentMethod) {
      return false;
    }

    if (this.order.error) {
      return false;
    }

    // will be used for restricting payezee payment process
    // if (
    //   this.order.creditCard &&
    //   this.order.creditCard.type === 1 &&
    //   (!this.order.creditCard.vendor ||
    //     this.order.creditCard.vendor === PaymentMethodVendor.Payeezy)
    // ) {
    //   return false;
    // }

    if (this.order.isCustom) {
      // cost center should be defined for custom order type
      return this.isCostCenterDefined;
    }

    if (this.authService.canEditSAP) {
      if (this.order.isByingWindow) {
        if (this.isSapType === SAP_TYPE.STANDARD) {
          return this.isCostCenterDefined;
        } else if (this.isSapType === SAP_TYPE.DECISION_POINT) {
          return this.isCostCenterDefined && this.isDPSelected;
        } else {
          return true;
        }
      }
    }

    if (this.isSapType === SAP_TYPE.STANDARD) {
      return this.isPaymentDefined;
    } else if (this.isSapType === SAP_TYPE.DECISION_POINT) {
      return this.isPaymentDefined && this.isDPSelected;
    } else {
      return true;
    }
  }

  get canCreateWSLROrder(): boolean {
    if (this.canSwitchOrderType === false) {
      return true;
    } else if (this.wholesalerSelection.id > 0) {
      return true;
    }
    return false;
  }

  private get isDPSelected(): boolean {
    return (
      !!this.order.attr_decision_point_id ||
      (this.dpSelection && this.dpSelection.id !== '0')
    );
  }

  private get isPaymentDefined(): boolean {
    return !!this.creditCard || this.isCostCenterDefined;
  }

  private get isCostCenterDefined(): boolean {
    return this.costCenter && this.costCenter.verified;
  }

  // apply sap
  async onApplyPayment(): Promise<ActiveToast<any>> {
    this.closeWithWarning = false;
    this.inProgress = true;

    if (this.creditCard) {
      if (this.creditCard.isValid) {
        if (this.saveCard) {
          await this.paymentService
            .saveCreditCardForFuture(this.order.id, this.creditCard.label)
            .toPromise();
        }
      } else {
        this.inProgress = false;
        return this.toastr.warning(
          'Cannot link the order with unverified card.'
        );
      }
    }

    const orderAttributes = {
      cart_name: this.orderName,
    };

    if (
      this.isSapType === SAP_TYPE.DECISION_POINT &&
      this.dpSelection &&
      this.dpSelection.id !== '0'
    ) {
      orderAttributes['decision_point_id'] = this.dpSelection.id;
      this.order.attr_decision_point_id = this.dpSelection.id;
      orderAttributes['cart_name'] = this.dpOrderName;
      this.order.attr_cart_name = this.dpOrderName;
      this.order.decisionPointData = {
        id: this.dpSelection.id,
        label: this.dpSelection.label,
        wslr_count: this.dpSelection.wslr_count,
        store_count: this.dpSelection.store_count,
      };

      orderAttributes['notes'] = this.dpOrderName;
      if (!this.poNumber) {
        this.poNumber = this.order.attr_cart_name;
      }
    }
    if (this.isTaxExemptAllowed) {
      orderAttributes['tax_exempt'] = this.taxExempt.toString();
    }

    this.order.attr_po_num = this.poNumber;
    orderAttributes['po_num'] = this.poNumber;

    if (this.isAttentionChanged) {
      this.order.attr_attention = this.attention || '';
      orderAttributes['attention'] = this.order.attr_attention;
    }

    const retOrder: Order = await this.orderService
      .setOrderAttributes(this.order, orderAttributes)
      .toPromise();
    this.inProgress = false;
    if (retOrder && !retOrder.error) {
      this.order = retOrder;
      this.hideSapModal(retOrder);
    }
  }

  // DP select handler
  selectDP(selection): void {
    this.dpSelection = selection;
    this.dpSelectionId = this.dpSelection.id;
    this.dpOrderName = `${this.dpSelection.label} ${this.orderService.getDefaultOrderName(this.order.type)}`;
  }

  // Wholesaler select handler
  selectWholesaler(selection): void {
    this.wholesalerSelection = selection;
    this.wholesalerSelectionId = this.wholesalerSelection.id;
    this.wslrOrderName = `WSLR #${selection.external_id} ${this.orderService.getDefaultOrderName(this.order.type)}`;
    // this.wslrAttention = selection;
  }

  // create new wholesaler
  createWholesalerOrder(): void {
    if (this.canSwitchOrderType) {
      if (this.wholesalerSelection.id > 0) {
        // update order entity id  and cart name
        this.order.attr_cart_name = this.wslrOrderName;
        this.order.entity_id = this.wholesalerSelection.id;
        this.order.isWSLR = true;

        const orderAttrs = {
          cart_name: this.wslrOrderName,
        };

        if (this.isAttentionChanged) {
          this.order.attr_attention = this.attention || '';
          orderAttrs['attention'] = this.order.attr_attention;
        }

        this.order.taxExempt = this.wslrTaxExempt;
        orderAttrs['tax_exempt'] = this.order.taxExempt.toString();

        this.inProgress = true;

        this.paymentService
          .updatePaymentMethod(this.order, null)
          .pipe(
            untilDestroyed(this),
            switchMap((retOrder) => {
              if (!retOrder) {
                return of(null);
              }
              return this.orderService.updateEntity(
                retOrder,
                this.wholesalerSelection.id
              );
            }),
            switchMap((retOrder) => {
              if (!retOrder) {
                return of(null);
              }
              return this.orderService.setOrderAttributes(retOrder, orderAttrs);
            })
          )
          .subscribe((order: Order) => {
            this.inProgress = false;
            if (order) {
              this.closeWithWarning = false;
              this.hideSapModal(order);
            }
          });
      }
    } else {
      // only allowed to change  order name
      const orderAttrs = {
        cart_name: this.wslrOrderName,
      };

      if (this.isAttentionChanged) {
        this.order.attr_attention = this.attention || '';
        orderAttrs['attention'] = this.order.attr_attention;
      }

      this.order.taxExempt = this.wslrTaxExempt;
      orderAttrs['tax_exempt'] = this.wslrTaxExempt.toString();

      this.order.attr_cart_name = this.wslrOrderName;
      this.order.isWSLR = true;
      this.inProgress = true;

      this.paymentService
        .updatePaymentMethod(this.order, null)
        .pipe(
          untilDestroyed(this),
          switchMap((retOrder) => {
            if (!retOrder) {
              return of(null);
            }
            return this.orderService.setOrderAttributes(retOrder, orderAttrs);
          })
        )
        .subscribe((retOrder) => {
          this.inProgress = false;
          this.hideSapModal(retOrder);
        });
    }
  }

  close(): void {
    this.hideSapModal(null);
  }

  onSavedCard(saveCard: boolean): void {
    this.saveCard = saveCard;
  }

  checkForAttentionChanges(): void {
    this.attentionFieldClicked = true;
  }

  onPaymentChanged(paymentMethod: PaymentMethod): void {
    this.paymentService
      .updatePaymentMethod(this.order, paymentMethod)
      .pipe(untilDestroyed(this))
      .subscribe((retOrder) => {
        if (retOrder) {
          this.order = retOrder;
          if (this.order.costCenter) {
            this.costCenter = this.order.costCenter;
            this.creditCard = null;
            this.setOrderName(this.costCenter.label);
          } else if (this.order.creditCard) {
            this.creditCard = this.order.creditCard;
            this.costCenter = null;
            this.setOrderName(this.creditCard.label);
          } else {
            this.costCenter = null;
            this.creditCard = null;
          }
        }
      });
  }

  // private methods

  // init DP dropdown
  private initDPSearch(): void {
    this.dpSearchSubject
      .pipe(
        untilDestroyed(this),
        debounceTime(400),
        filter((term) => !!term),
        tap(() => (this.dpSearchLoading = true)),
        switchMap((term) => this.findDecisionPoints(term)),
        catchError(() => of([])), // empty list on error
        tap(() => {
          this.dpSearchLoading = false;
        })
      )
      .subscribe((result) => {
        this.dpSearchResults = result || [];
      });
  }

  // init Wholesaler dropdown
  private initWholesalerSearch(): void {
    this.wholesalerSearchSubject
      .pipe(
        untilDestroyed(this),
        debounceTime(400),
        filter((term) => !!term),
        tap(() => (this.wholesalerSearchLoading = true)),
        switchMap((term: string) => this.findWholeSalers(term)),
        catchError(() => of([])), // empty list on error
        tap(() => {
          this.wholesalerSearchLoading = false;
        })
      )
      .subscribe((result) => {
        this.wholesalerSearchResults = result || [];
      });
  }

  // search for wholesaler
  private findWholeSalers(searchingName: string): Observable<Entity[]> {
    return this.entityService.search(searchingName);
  }

  // search for DP
  private findDecisionPoints(search: string): Observable<DecisionPointData[]> {
    if (!search) {
      return of([]);
    }
    return this.productService.searchDecisionPoints(search);
  }

  private setOrderName(val: string): void {
    if (!this.originalOrderName) {
      this.orderName = val;
    }
  }

  get canShowTaxExempt(): boolean {
    return this.authService.isWholeSaler || this.order.isWSLR;
  }

  public get isTaxExemptAllowed(): boolean {
    return this.authService.user.isTaxExemptAllowed;
  }

  public get isWSLRTaxExemptAllowed(): boolean {
    return this.wholesalerSelection?.allow_tax_exempt === true;
  }

  get canShowWSLRTaxExempt(): boolean {
    return this.isWSLRTaxExemptAllowed;
  }

  public get isAttentionChanged(): boolean {
    if (!this.order) {
      return false;
    }

    return (
      this.attentionFieldClicked || this.originalAttention !== this.attention
    );
  }

  get verifyingSAP(): boolean {
    return this.paymentService.verifyingSAP;
  }

  get updatingPaymentMethod(): boolean {
    return this.paymentService.updatingPaymentMethod;
  }

  get canAddDecisionPointOrder(): boolean {
    return (
      this.authService.canEditDecisionPoints &&
      this.order.type !== OrderTypes.CUSTOM_TYPE
    );
  }
}
